<template>
  <!-- ===================================================================
       FICHE D'ACTIVITÉ — écran de l'application, balisage propre.
       Le site garde le sien, intact, dans le `v-else` plus bas.

       Photo : les visuels d'offre plafonnent à 414x219 côté serveur. Étalés
       en couverture pleine largeur — le réflexe habituel sur ce type de
       fiche — ils seraient flous. La photo nette est donc contenue à 320px
       de large au maximum, posée sur un fond constitué de la même image
       floutée : on obtient une couverture pleine largeur sans jamais étirer
       le fichier au-delà de sa définition.

       Dates et options sont AFFICHÉES ici, mais leur sélection appartient au
       tunnel de réservation (OfferDetailsPageV2), qui gère le paiement.
       Les dupliquer ici désynchroniserait les deux écrans.
       =================================================================== -->
  <div v-if="isNative && !loading && !error" class="fd-page">

    <header class="fd-nav">
      <button type="button" class="fd-nav__btn" aria-label="Retour" @click="$router.back()">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <span v-if="od.sport" class="fd-nav__sport">{{ od.sport }}</span>
      <button type="button" class="fd-nav__btn" aria-label="Partager cette activité" @click="fdPartager">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="2.6"/><circle cx="6" cy="12" r="2.6"/><circle cx="18" cy="19" r="2.6"/><path d="m8.4 13.4 7.2 4.2M15.6 6.4 8.4 10.6"/></svg>
      </button>
    </header>

    <section v-if="fdPhotos.length" class="fd-photo">
      <div class="fd-photo__frame">
        <img
          class="fd-photo__main"
          :src="apiImg(imageOriginale(fdPhotos[fdPhotoActive]))"
          :alt="od.titleOffers || ''"
          loading="eager"
          fetchpriority="high"
        >
        <span v-if="od.type" class="fd-photo__tag">{{ od.type }}</span>
      </div>

      <div v-if="fdPhotos.length > 1" class="fd-photo__strip">
        <button
          v-for="(ph, i) in fdPhotos" :key="ph"
          type="button" class="fd-photo__thumb"
          :class="{ 'is-on': i === fdPhotoActive }"
          :aria-label="`Photo ${i + 1}`"
          @click="fdPhotoActive = i"
        ><img :src="apiImg(ph)" alt="" loading="lazy"></button>
      </div>
    </section>

    <section class="fd-head">
      <h1 class="fd-head__title" v-html="od.titleOffers || offerDetails.presentation"></h1>

      <div v-if="fdAvis.moyenne" class="fd-head__rating">
        <span class="fd-stars" :aria-label="`Note ${fdAvis.moyenne} sur 5`">
          <svg v-for="n in 5" :key="n" viewBox="0 0 24 24" width="15" height="15"
               :fill="n <= Math.round(fdAvis.moyenne) ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.6">
            <path d="m12 3.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.7l5.8-.8z"/>
          </svg>
        </span>
        <span class="fd-head__score">{{ String(fdAvis.moyenne).replace('.', ',') }}</span>
        <span class="fd-head__count">{{ fdAvis.total }} avis</span>
      </div>

      <div class="fd-head__tags">
        <span v-if="od.finalCity" class="fd-tag">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round"><path d="M20.5 10.5c0 6.5-8.5 12-8.5 12s-8.5-5.5-8.5-12a8.5 8.5 0 0 1 17 0z"/><circle cx="12" cy="10.3" r="2.8"/></svg>
          {{ od.finalCity }}<template v-if="od.finalPostcode"> ({{ String(od.finalPostcode).slice(0, 2) }})</template>
        </span>
        <span v-if="fdNiveau" class="fd-tag">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5.5l3.5 2"/></svg>
          {{ fdNiveau }}
        </span>
        <span v-if="od.type" class="fd-tag">{{ od.type }}</span>
      </div>
    </section>

    <section v-if="fdDates.length" class="fd-card">
      <div class="fd-card__head">
        <h2 class="fd-card__title">{{ fdEstSejour ? 'Dates du séjour' : 'Dates proposées' }}</h2>
        <span v-if="!fdEstSejour" class="fd-card__badge">
          {{ fdDates.length }} {{ fdDates.length > 1 ? 'dates' : 'date' }}
        </span>
      </div>

      <!-- Séjour : l'API décrit un séjour par une ligne de critères PAR JOURNÉE.
           Les afficher comme autant de dates laissait croire qu'on pouvait
           réserver le 31 octobre seul, et répétait trois fois les mêmes places.
           On n'expose donc qu'une période, non sélectionnable. -->
      <div v-if="fdEstSejour" class="fd-date fd-date--periode">
        <span class="fd-date__left">
          <span class="fd-date__day">{{ fdPeriode.libelle }}</span>
          <span class="fd-date__spots">
            {{ fdPeriode.places > 0
              ? `${fdPeriode.places} place${fdPeriode.places > 1 ? 's' : ''} restante${fdPeriode.places > 1 ? 's' : ''}`
              : 'Complet' }}
          </span>
        </span>
        <span v-if="fdPeriode.prix" class="fd-date__price">{{ Math.round(fdPeriode.prix) }} €</span>
      </div>

      <!-- Sortie ou session : chaque date est une réservation distincte, donc
           un choix réel. Il se faisait auparavant dans le tunnel, où le bouton
           « Date » n'était relié à rien et où la modale n'était jamais ouverte :
           la date ne venait que de l'adresse construite par le catalogue. On la
           choisit donc ici, seul endroit qui montre places et tarif. -->
      <ul v-else class="fd-dates">
        <li v-for="d in fdDates" :key="d.jour">
          <button
            type="button"
            class="fd-date fd-date--choix"
            :class="{ 'is-full': d.places <= 0, 'is-selected': d.brut === fdDateChoisie }"
            :disabled="d.places <= 0"
            :aria-pressed="String(d.brut === fdDateChoisie)"
            @click="fdChoisirDate(d)"
          >
            <span class="fd-date__left">
              <span class="fd-date__day">{{ d.jour }}</span>
              <span class="fd-date__spots">
                {{ d.places > 0
                  ? `${d.places} place${d.places > 1 ? 's' : ''} restante${d.places > 1 ? 's' : ''}`
                  : 'Complet' }}
              </span>
            </span>
            <span v-if="d.prix" class="fd-date__price">{{ Math.round(d.prix) }} €</span>
          </button>
        </li>
      </ul>

      <p class="fd-card__note">
        {{ fdEstSejour ? 'Le séjour se réserve en entier.' : 'Choisissez votre date, puis réservez.' }}
      </p>
    </section>

    <section v-if="offerDetails.details" class="fd-card">
      <div class="fd-card__head"><h2 class="fd-card__title">À propos</h2></div>
      <div class="fd-desc" :class="{ 'is-clipped': !fdDescriptionOuverte }" v-html="offerDetails.details"></div>
      <button type="button" class="fd-more" @click="fdDescriptionOuverte = !fdDescriptionOuverte">
        {{ fdDescriptionOuverte ? 'Réduire' : 'Lire la suite' }}
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
             :style="fdDescriptionOuverte ? 'transform:rotate(180deg)' : ''"><path d="m6 9 6 6 6-6"/></svg>
      </button>
    </section>

    <section v-if="fdInclus.length" class="fd-card">
      <div class="fd-card__head"><h2 class="fd-card__title">Ce qui est inclus</h2></div>
      <ul class="fd-inclus">
        <li v-for="(item, i) in fdInclus" :key="i" class="fd-inclus__item">
          <span class="fd-inclus__check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"/></svg>
          </span>
          <span v-html="item"></span>
        </li>
      </ul>
    </section>

    <section v-if="fdOptions.length" class="fd-card">
      <div class="fd-card__head"><h2 class="fd-card__title">Options disponibles</h2></div>
      <ul class="fd-inclus">
        <li v-for="(item, i) in fdOptions" :key="i" class="fd-inclus__item fd-inclus__item--paid">
          <span class="fd-inclus__plus" aria-hidden="true">+</span>
          <span v-html="item"></span>
        </li>
      </ul>
      <p class="fd-card__note">À sélectionner lors de la réservation.</p>
    </section>

    <!-- Programme détaillé : la plaquette PDF hébergée sur Google Drive,
         affichée en mode aperçu, page après page, directement dans l'écran. -->
    <section v-if="googleDriveLinkFormatted" class="fd-card fd-card--pdf">
      <div class="fd-card__head">
        <h2 class="fd-card__title">Programme détaillé</h2>
        <a :href="googleDriveLinkFormatted" target="_blank" rel="noopener" class="fd-card__link">Plein écran</a>
      </div>
      <div class="fd-pdf">
        <iframe
          :src="googleDriveLinkFormatted"
          title="Programme détaillé du séjour"
          loading="lazy"
          allow="autoplay"
        ></iframe>
      </div>
    </section>

    <section v-if="fdClubNom || fdAdresse" class="fd-card">
      <div class="fd-card__head"><h2 class="fd-card__title">Organisateur et lieu</h2></div>

      <div v-if="fdClubNom" class="fd-club">
        <span class="fd-club__mark" aria-hidden="true">{{ fdInitiales }}</span>
        <span class="fd-club__text">
          <span class="fd-club__name">{{ fdClubNom }}</span>
          <span class="fd-club__sub">Partenaire OuiCSport</span>
        </span>
      </div>

      <div v-if="fdAdresse" class="fd-lieu">
        <a class="fd-adresse" :href="fdLienCarte" target="_blank" rel="noopener">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round"><path d="M20.5 10.5c0 6.5-8.5 12-8.5 12s-8.5-5.5-8.5-12a8.5 8.5 0 0 1 17 0z"/><circle cx="12" cy="10.3" r="2.8"/></svg>
          <span class="fd-adresse__texte">{{ fdAdresse }}</span>
        </a>

        <!-- Apercu cartographique. L'iframe ne recoit aucun evenement
             (pointer-events: none) : un doigt pose dessus fait defiler la page
             comme ailleurs, et le toucher declenche le lien qui l'englobe. Sans
             cela, la carte capterait le geste et bloquerait le defilement. -->
        <a
          class="fd-carte"
          :href="fdLienCarte"
          target="_blank" rel="noopener"
          :aria-label="`Ouvrir ${fdAdresse} dans l'application de cartes`"
        >
          <iframe
            class="fd-carte__vue"
            :src="fdCarteApercu"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            tabindex="-1"
            aria-hidden="true"
            title=""
          ></iframe>
          <span class="fd-carte__pastille">Voir sur la carte</span>
        </a>
      </div>
    </section>

    <section v-if="fdAvis.total" class="fd-card">
      <div class="fd-card__head">
        <h2 class="fd-card__title">Avis</h2>
        <span class="fd-card__badge">{{ fdAvis.total }} avis</span>
      </div>

      <div class="fd-note">
        <span class="fd-note__big">{{ String(fdAvis.moyenne).replace('.', ',') }}</span>
        <span class="fd-note__meta">
          <span class="fd-stars">
            <svg v-for="n in 5" :key="n" viewBox="0 0 24 24" width="13" height="13"
                 :fill="n <= Math.round(fdAvis.moyenne) ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.6">
              <path d="m12 3.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.7l5.8-.8z"/>
            </svg>
          </span>
          <span class="fd-note__sub">Sur {{ fdAvis.total }} participants</span>
        </span>
      </div>

      <ul v-if="fdAvis.liste.length" class="fd-avis">
        <li v-for="(a, i) in fdAvis.liste" :key="i" class="fd-avis__item">
          <div class="fd-avis__head">
            <span class="fd-avis__who">{{ a.firstName }} {{ (a.lastName || '').charAt(0) }}.</span>
            <span class="fd-avis__when">{{ (a.datetime || '').split(' ')[0] }}</span>
          </div>
          <span class="fd-stars fd-stars--small">
            <svg v-for="n in 5" :key="n" viewBox="0 0 24 24" width="11" height="11"
                 :fill="n <= Number(a.note) ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="1.8">
              <path d="m12 3.6 2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.7l5.8-.8z"/>
            </svg>
          </span>
          <p class="fd-avis__text">{{ a.comment }}</p>
        </li>
      </ul>
    </section>

    <p v-if="offerDetails.cgvLink" class="fd-legal">
      <a :href="offerDetails.cgvLink" target="_blank" rel="noopener">Conditions générales et annulation</a>
    </p>

    <!-- Barre d'action, posée au-dessus de la barre d'onglets. -->
    <div class="fd-bar">
      <span class="fd-bar__price">
        <span class="fd-bar__label">À partir de</span>
        <span class="fd-bar__value">{{ offerDetails.price }} <span>€</span></span>
      </span>
      <RouterLink
        v-if="isLoggedIn"
        class="fd-bar__cta"
        :to="fdCible"
      >
        Réserver
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13M12.5 5.5 19 12l-6.5 6.5"/></svg>
      </RouterLink>
      <RouterLink v-else class="fd-bar__cta" to="/login">Se connecter</RouterLink>
    </div>
  </div>

  <div v-else-if="isNative && loading" class="fd-page fd-page--wait" aria-hidden="true">
    <span class="fd-ghost fd-ghost--photo"></span>
    <span class="fd-ghost fd-ghost--line"></span>
    <span class="fd-ghost fd-ghost--line fd-ghost--short"></span>
    <span class="fd-ghost fd-ghost--block"></span>
  </div>

  <div v-else-if="isNative && error" class="fd-page fd-page--wait">
    <p class="fd-erreur">Impossible de charger cette activité. Vérifie ta connexion et réessaie.</p>
  </div>

  <!-- ===== Site web : balisage d'origine, strictement inchangé ===== -->
  <template v-else>

  <div v-if="loading" class="loading-overlay">
    <div class="spinner"></div>
  </div>

  <div v-else-if="error" class="offer-detail-error">
    Une erreur est survenue lors du chargement des détails de l'offre.
  </div>

  <div v-else>
    <section class="hero3" 
      :style="offerDetails.offerDetails.sport 
        ? `background: url('${sportBanner(offerDetails.offerDetails.sport)}') no-repeat center 50%/cover;`
        : ''">
    </section>

    <div class="activity-details">
      <div class="activity-details-top" :style="{ gridTemplateColumns: !isMenuVisible ? '1fr 2fr 1fr' : 'none' }">
        <h2 v-if="!isMenuVisible">{{ offerDetails.offerDetails.sport }} <br v-if="!isMenuVisible">
          <span v-if="!isStageSejour()">
            {{ formattedDates[0]}}
            <template v-if="formattedDates[0] !== formattedDates[formattedDates.length - 1]">
              au {{ formattedDates[formattedDates.length - 1]  }}
            </template>
          </span>
          <span v-if="isStageSejour()">
            {{ formattedDates[0] }} 
            <template v-if="formattedDates[0] !== formattedDates[formattedDates.length - 1]">
              au {{ formattedDates[formattedDates.length - 1] }}
            </template>
          </span>
        </h2>
        <h3 v-if="offerDetails.offerDetails.titleOffers" v-html="offerDetails.offerDetails.titleOffers"></h3>
        <h3 v-else v-html="offerDetails.presentation"></h3>
        <h2 v-if="!isMenuVisible" style="text-align: right;">{{ offerDetails.offerDetails.type }} <br v-if="!isMenuVisible">{{ offerDetails.offerDetails.finalCity }} ({{ offerDetails.offerDetails.finalPostcode }})</h2>
        <!-- Natif : les deux <h2> ci-dessus sont masqués sous 768px (isMenuVisible).
             On restitue la même information sous forme de pastilles lisibles. -->
        <div v-if="isNative" class="offer-meta-chips">
          <span v-if="offerDetails.offerDetails.sport" class="oc-chip">{{ offerDetails.offerDetails.sport }}</span>
          <span v-if="offerDetails.offerDetails.type" class="oc-chip">{{ offerDetails.offerDetails.type }}</span>
          <span v-if="offerDetails.offerDetails.finalCity" class="oc-chip">{{ offerDetails.offerDetails.finalCity }} ({{ offerDetails.offerDetails.finalPostcode }})</span>
        </div>
      </div>
      <div class="activity-details-description">
        <p class="activity-location"> 
    <span v-if="isStageSejour() && formattedDates.length > 1">
      Du {{ formattedDates[0] }} au {{ formattedDates[formattedDates.length - 1] }}
    </span>
    <span v-else-if="isStageSejour()">
      Le {{ formattedDates[0] }}
    </span>
    <span v-else>
        Le {{ formattedDates[0] }} {{ findScheduleByDate(dateSelected) }}
    </span>
    : <br v-if="!isMenuVisible">
          <a :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(offerDetails.offerDetails.finalAddress + ' ' + offerDetails.offerDetails.finalCity + ' ' + offerDetails.offerDetails.finalPostcode)" target="_blank" style="text-decoration: underline;">
            <i class="fa fa-map-marker"></i>
            {{ offerDetails.offerDetails.finalAddress }} {{ offerDetails.offerDetails.finalCity }} {{ offerDetails.offerDetails.finalPostcode }}
          </a>
        </p>

        <div class="image_activity">
          <img :src="offerDetails.images[0].replace('187x97/', '').replace('414x219/', '')"/>
        </div>
        <br v-if="!isMenuVisible"><br v-if="!isMenuVisible">
        <p  class="activity-location">Organisé par Ouicsport</p> <br v-if="!isMenuVisible">
        <p v-html="offerDetails.details" class="p-description-wire"></p>
        <br v-if="!isMenuVisible"><br v-if="!isMenuVisible">
        <div v-if="imagesValid > 1" class="offer-gallery-grid">
          <div class="image_activity_preview" v-for="(image, index) in offerDetails.images.slice(1, imagesValid)" :key="index" @click="openModal(index)">
            <img :src="image.replace('187x97/', '').replace('414x219/', '')" />
          </div>
        </div>
        
      <section class="tariff-information">
        <div class="price-tag">
          <h2 v-if="!isStageSejour() && findPriceByDate(dateSelected) !== 'N/A' && findPriceByDate(dateSelected).includes('-')">
            TARIF : De {{ findPriceByDate(dateSelected).split('-')[0] }}€ à {{ findPriceByDate(dateSelected).split('-')[1] }}€
          </h2>
          <h2 v-else-if="!isStageSejour()">
            TARIF : {{ findPriceByDate(dateSelected) }}€
          </h2>
          <h2 v-else-if="offerDetails.filteredCriteria && offerDetails.filteredCriteria.length > 0">
            TARIF : {{ offerDetails.filteredCriteria[0].price }}€
          </h2>
        </div>
        <div class="inclusions" v-if="offerDetails.freeEquipments && offerDetails.freeEquipments.length > 0">
          <h3>INCLUS</h3>
          <ul>
            <li v-for="equipment in offerDetails.freeEquipments" :key="equipment" v-html="equipment">
            </li>
          </ul>
        </div>
        <div class="exclusions" v-if="offerDetails.paidEquipments && offerDetails.paidEquipments.length > 0">
          <h3>NON INCLUS</h3>
          <ul v-html="getPaidEquipmentsHTML()"></ul>

        </div>
        
        
      </section>
      <p v-if="googleDriveLinkFormatted" style="text-align: center; margin-top: 20px; margin-bottom: 20px;">
        <a :href="googleDriveLinkFormatted" target="_blank" class="offer-brochure-link" style="color: blue; font-weight: bold; text-decoration: underline; font-size: clamp(1rem, 2vw, 1.5rem); ">
          Voir la plaquette du séjour
        </a>
      </p>

      <iframe v-if="googleDriveLinkFormatted" 
              :src="googleDriveLinkFormatted" 
              width="100%" 
              height="600px" 
              frameborder="0">
      </iframe>
      <br v-if="!isMenuVisible"><br v-if="!isMenuVisible">
      <div class="reservation-button">
          <RouterLink
            :to="{ name: 'OfferDetailsPageV2', params: { id: idofferData } }"
            v-if="isLoggedIn"
            custom
            v-slot="{ navigate }">
            <button type="button" class="btn-selected" :disabled="!isLoggedIn" @click="navigate">RÉSERVER</button>
          </RouterLink>
          <RouterLink
            to="/login"
            v-else
            custom
            v-slot="{ navigate }">
            <button type="button" class="btn-selected"  @click="navigate">Se connecter</button>
          </RouterLink>
        </div>

        <div class="reservation-button">
          <p v-if="!isLoggedIn" class="login-warning">Veuillez vous connecter !</p>
        </div>
      </div>
     
    </div>

    <!-- Natif : barre d'action ancrée juste au-dessus de la barre d'onglets.
         Elle reprend le prix et l'action principale ; le bouton en ligne plus
         haut est masqué en natif (CSS) pour ne pas dupliquer l'appel à l'action. -->
    <div v-if="isNative" class="oc-actionbar oc-actionbar--offer">
      <div
        class="oc-actionbar__price"
        v-if="isStageSejour()
          ? (offerDetails.filteredCriteria && offerDetails.filteredCriteria.length > 0)
          : findPriceByDate(dateSelected) !== 'N/A'">
        <span class="oc-actionbar__price-label">Tarif</span>
        <span class="oc-actionbar__price-value">
          <template v-if="!isStageSejour() && findPriceByDate(dateSelected).includes('-')">dès {{ findPriceByDate(dateSelected).split('-')[0] }} €</template>
          <template v-else-if="!isStageSejour()">{{ findPriceByDate(dateSelected) }} €</template>
          <template v-else>{{ offerDetails.filteredCriteria[0].price }} €</template>
        </span>
      </div>
      <RouterLink
        :to="{ name: 'OfferDetailsPageV2', params: { id: idofferData } }"
        v-if="isLoggedIn"
        custom
        v-slot="{ navigate }">
        <button type="button" class="oc-btn oc-btn--primary oc-actionbar__cta" @click="navigate">Réserver</button>
      </RouterLink>
      <RouterLink
        to="/login"
        v-else
        custom
        v-slot="{ navigate }">
        <button type="button" class="oc-btn oc-btn--primary oc-actionbar__cta" @click="navigate">Se connecter</button>
      </RouterLink>
    </div>

    <!-- Modal -->
    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <button class="close-button" @click="closeModal">X</button>
        <div class="modal-images">
          <div v-for="(image, index) in offerDetails.images" :key="index" class="modal-image">
            <img :src="image.replace('187x97/', '').replace('414x219/', '')" />
          </div>
        </div>
      </div>
    </div>
  </div>
  </template>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { apiImg, imageOriginale, sportBanner, isNative } from '@/api/base';
import { shareOffer } from '@/native/share';
import { isIOS } from '@/native/platform';


export default {
  name: 'OfferDetails',
  props: ['offerId'],
  data() {
    return {
      isModalOpen: false,
      hoveredSport: null,
      currentIndex: 0,
      isMenuVisible: false,
    };
  },
  methods: {
    checkScreenWidth() {

      if (window.innerWidth < 768) {
        this.isMenuVisible = true;
      } else {
        this.isMenuVisible = false;
      }

    },
    getPaidEquipmentsHTML() {
    if (!this.offerDetails || !this.offerDetails.paidEquipments) return '';
    return this.offerDetails.paidEquipments
      .map(e => `<li>${e.name.replace(/'/g, "&rsquo;")} : ${e.price} €</li>`)
      .join('');
  },
    openModal(index) {
      this.currentIndex = index;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    findPriceByDate(searchDate) {
      const criteria = this.offerDetails.criteria || [];
      // Trouver tous les critères pour cette date (match exact ou startsWith pour dates groupées)
      const matching = criteria.filter(c => c.date === searchDate || c.date.startsWith(searchDate + ' '));
      if (matching.length === 0) return 'N/A';
      const prices = matching.map(c => parseFloat(c.price)).filter(p => !isNaN(p));
      if (prices.length === 0) return 'N/A';
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      if (minPrice === maxPrice) return minPrice.toFixed(2);
      return `${minPrice.toFixed(2)}-${maxPrice.toFixed(2)}`;
    },
    findScheduleByDate(searchDate) {
      const criteria = this.offerDetails.criteria || [];
      const filteredCriteria = criteria.find(criterion => criterion.date === searchDate)
                            || criteria.find(criterion => criterion.date.startsWith(searchDate + ' '));
      
      if (!filteredCriteria) {
        return '';
      }

      const scheduleCriterion = filteredCriteria.criterion.find(c => c.Name === 'Horaire');
      
      if (!scheduleCriterion) {
        return '';
      }

      const [startTime, endTime] = scheduleCriterion.Value.split(' - ');
      return `de ${startTime} à ${endTime}`;
    },

    choice_service(serviceId) {
      this.activeService = serviceId;
    },
    formatAddress(address, postcode) {
      const fullAddress = `${address}, ${postcode}`;
      return encodeURIComponent(fullAddress);
    },
    previousMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
    },
    isSelectedDate(date) {
      return this.selectedDate === date;
    },
    selectDateFromFormatted(date) {
      this.selectedDetailUniqueProp = null;
      this.selectedDetails = null;
      this.selectedDate = date;
    },
    formatDateDisplay(date) {
      const [day, month] = date.split('/');
      return `Mar ${day}/${month}`;
    },
    getOfferDetailsForSelectedDate() {
      if (!this.selectedDate || !this.offerDetails || !this.offerDetails.criteria) {
        return [];
      }

      return this.offerDetails.criteria.filter(criterion => {
        if (typeof criterion.date !== 'string') {
          return false;
        }

        const [datePart] = criterion.date.split(' ');
        const formattedDate = datePart.trim();

        return formattedDate === this.selectedDate.trim();
      });
    },
    handleReservation() {
      this.selectedDetails.quantite = this.quantite;
      this.selectedDetails.price = this.totalPrix;
      this.showStripePayment = true; 
    },
    isDisabledDay() {
      return false;
    },
    selectDetail(detail) {
      this.price = detail.price;
      this.selectedDetails = detail;
      this.selectedDetailUniqueProp = detail.id;
      this.activeService = 'service2';
    },
    isStageSejour() {
      return this.offerDetails && (this.offerDetails.offerDetails.type === "Stage/Séjour" || this.offerDetails.offerDetails.type === "Séjour" ||  this.offerDetails.offerDetails.type === "Stage" ||  this.offerDetails.offerDetails.type === "Stage/Séjour");
    }
  },
  setup(props) {
  const offerDetails = ref(null);
  const formattedDates = ref([]);
  const loading = ref(true);
  const error = ref(false);
  const imagesValid = ref(null);
  const store = useStore();
  const currentMonth = ref(new Date().getMonth());
  const currentYear = ref(new Date().getFullYear());
  const idofferData = ref(null);
  const dateSelected = ref(null);
  const googleDriveLinkFormatted = ref(null); // Ajout pour formater le lien Google Drive

  const isLoggedIn = computed(() => store.state.auth.user !== null);

  function findCriteriaByDate(criteria, searchDate) {
    // Match exact d'abord, puis startsWith pour les dates groupées (sans heure)
    const exact = criteria.filter(criterion => criterion.date === searchDate);
    if (exact.length > 0) return exact;
    return criteria.filter(criterion => criterion.date.startsWith(searchDate + ' ') || criterion.date === searchDate);
  }

  function combineFilteredCriteriaWithDetails(details, filteredCriteria) {
    let detailsCopy = JSON.parse(JSON.stringify(details));
    delete detailsCopy.criteria;
    detailsCopy.filteredCriteria = filteredCriteria;
    return detailsCopy;
  }

  onMounted(async () => {
    try {
      idofferData.value = props.offerId;
      var offerID = props.offerId;
      var parts = offerID.split(' ');
      var id = parts[0];
      // Gérer le cas où ID_Criteria n'a que la date sans l'heure (dates groupées)
      var date = parts.length >= 3 ? parts[1] + ' ' + parts[2] : parts[1];

      const response = await axios.get(`https://www.ouicsport.fr/api/search/offer-details-json/${id}`);
      offerDetails.value = JSON.parse(response.data["0"]);

      if (offerDetails.value.offerDetails && offerDetails.value.offerDetails.googleDriveLink) {
        const link = offerDetails.value.offerDetails.googleDriveLink;
        if (link.includes('drive.google.com/file/d/')) {
          googleDriveLinkFormatted.value = link.replace('/view?usp=sharing', '/preview');
        }
      }

      if (offerDetails.value.offerDetails && (offerDetails.value.offerDetails.type === "Stage à la carte" || offerDetails.value.offerDetails.type === 'Séjour' || offerDetails.value.offerDetails.type === 'Stage' || offerDetails.value.offerDetails.type === 'Stage/Séjour')) {
        offerDetails.value.filteredCriteria = JSON.parse(JSON.stringify(offerDetails.value.criteria));
        delete offerDetails.value.criteria;

        formattedDates.value = offerDetails.value.filteredCriteria.map(item => {
          const [day, month, year] = item.date.split(' ')[0].split('/');
          const dateObj = new Date(year, month - 1, day);
          return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(dateObj);
        });

      } else {
        var specificCriteria = findCriteriaByDate(offerDetails.value.criteria, date);

        if (specificCriteria && specificCriteria.length > 0) {
          formattedDates.value = specificCriteria.map(item => {
            const [day, month, year] = item.date.split(' ')[0].split('/');
            const dateObj = new Date(year, month - 1, day);
            return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(dateObj);
          });
        }

        combineFilteredCriteriaWithDetails(offerDetails.value, specificCriteria);
      }

      imagesValid.value = offerDetails.value.images ? offerDetails.value.images.length : 0;
      dateSelected.value = date
    } catch (err) {
      console.error("Erreur lors de la récupération des détails de l'offre:", err);
      error.value = true;
    } finally {
      loading.value = false;
    }
  });

  /* ====================================================================
     FICHE D'ACTIVITÉ — données de l'écran natif.
     Rien de ce qui suit ne concerne le site, qui garde son propre balisage.
     ==================================================================== */

  const od = computed(() => offerDetails.value?.offerDetails || {});

  /* Selon le type d'offre, le composant déplace `criteria` dans
     `filteredCriteria` puis supprime l'original (voir onMounted). Les deux
     sources doivent donc être lues, sinon les stages et séjours — ceux qui
     ont justement plusieurs dates — n'en affichent aucune. */
  const fdCriteres = computed(() =>
    offerDetails.value?.criteria || offerDetails.value?.filteredCriteria || []);

  /** Note moyenne et nombre d'avis, calculés sur les notations réelles. */
  const fdAvis = computed(() => {
    const list = (offerDetails.value?.notations || []).filter((n) => Number(n.note) > 0);
    if (!list.length) return { total: 0, moyenne: null, liste: [] };
    const somme = list.reduce((acc, n) => acc + Number(n.note), 0);
    return {
      total: list.length,
      moyenne: Math.round((somme / list.length) * 10) / 10,
      // Un commentaire vide ou « Aucun commentaire » n'apporte rien à l'écran :
      // on ne montre que les avis réellement rédigés.
      liste: list
        .filter((n) => n.comment && n.comment.trim() && n.comment.trim().toLowerCase() !== 'aucun commentaire')
        .slice(0, 3),
    };
  });

  /**
   * Dates proposées, avec prix et places restantes. Regroupées par jour.
   *
   * `brut` conserve la date telle que l'API l'écrit (« 30/10/2026 00:00 ») :
   * c'est cette forme exacte que le tunnel de réservation recherche dans les
   * critères, et une date reformatée n'y correspondrait plus.
   */
  const fdDates = computed(() => {
    const parJour = new Map();
    for (const c of fdCriteres.value) {
      const jour = String(c.date || '').split(' ')[0];
      if (!jour) continue;
      const prix = parseFloat(c.price) || 0;
      // Un créneau surréservé renvoie un stock négatif (« -4 ») : ramené à 0,
      // sans quoi il viendrait retrancher les places réelles d'un autre niveau
      // du même jour et sous-estimer la disponibilité affichée.
      const brut = parseInt(c.quantite, 10) || 0;
      const places = brut > 0 ? brut : 0;
      const dejaLa = parJour.get(jour);
      if (dejaLa) {
        dejaLa.places += places;
        dejaLa.prix = Math.min(dejaLa.prix, prix);
      } else {
        parJour.set(jour, { jour, prix, places, brut: c.date });
      }
    }
    const enTemps = (fr) => { const [j, m, a] = fr.split('/').map(Number); return new Date(a, m - 1, j).getTime(); };
    const aujourdhui = new Date(); aujourdhui.setHours(0, 0, 0, 0);
    return [...parJour.values()]
      .filter((d) => enTemps(d.jour) >= aujourdhui.getTime())
      .sort((a, b) => enTemps(a.jour) - enTemps(b.jour));
  });

  /**
   * Un stage ou un séjour se réserve en entier : ses journées ne sont pas des
   * dates au choix. Le test reprend les libellés exacts posés par le back.
   */
  const TYPES_SEJOUR = ['Stage', 'Séjour', 'Stage/Séjour'];
  const fdEstSejour = computed(
    () => TYPES_SEJOUR.includes(offerDetails.value?.offerDetails?.type),
  );

  /** Période couverte par un séjour, en une seule ligne. */
  const fdPeriode = computed(() => {
    const jours = fdDates.value;
    if (!jours.length) return { libelle: '', places: 0, prix: 0 };
    const premier = jours[0];
    const dernier = jours[jours.length - 1];
    return {
      libelle: jours.length > 1 ? `Du ${premier.jour} au ${dernier.jour}` : premier.jour,
      // Les places sont les mêmes d'une journée à l'autre — c'est le même
      // séjour. Les additionner triplerait le stock affiché.
      places: premier.places,
      prix: premier.prix,
    };
  });

  /**
   * Date transportée par l'adresse. Le catalogue construit un paramètre
   * composite « identifiant + date », que le tunnel de réservation redécoupe.
   * Un lien profond, lui, n'apporte que l'identifiant.
   */
  const fdDateDeLAdresse = computed(() => {
    const parts = String(props.offerId || '').split(' ');
    if (parts.length >= 3) return `${parts[1]} ${parts[2]}`;
    return parts[1] || null;
  });

  const fdDateManuelle = ref(null);
  const fdDateChoisie = computed(() => fdDateManuelle.value || fdDateDeLAdresse.value);

  const fdChoisirDate = (d) => {
    if (!d || d.places <= 0) return;
    fdDateManuelle.value = d.brut;
  };

  /**
   * Adresse du tunnel de réservation, reconstruite avec la date retenue.
   *
   * Sans date, le tunnel s'ouvre entièrement vide : il cherche le critère
   * correspondant, ne trouve rien, et n'affiche ni quantité, ni options, ni
   * bouton. On lui garantit donc toujours une date.
   */
  const fdCible = computed(() => {
    const brut = String(idofferData.value || '');
    const id = brut.split(' ')[0];
    const versTunnel = (param) => ({ name: 'OfferDetailsPageV2', params: { id: param } });

    if (fdEstSejour.value) {
      if (fdDateDeLAdresse.value) return versTunnel(brut);
      const premier = fdDates.value[0];
      return versTunnel(premier ? `${id} ${premier.brut}` : brut);
    }

    const date = fdDateChoisie.value || (fdDates.value.find((d) => d.places > 0) || {}).brut;
    return versTunnel(date ? `${id} ${date}` : brut);
  });

  /** Niveau requis, lu dans les critères. */
  const fdNiveau = computed(() => {
    for (const c of fdCriteres.value) {
      const v = (c.criterion || []).find((x) => x.Name === 'Niveaux');
      if (v && v.Value) return v.Value;
    }
    return null;
  });

  const fdInclus = computed(() => (offerDetails.value?.freeEquipments || []).filter(Boolean));
  const fdOptions = computed(() => (offerDetails.value?.paidEquipments || []).filter(Boolean));

  /**
   * L'API rend ses textes deja echappes en HTML : « l&#039;Ecole », « Sport
   * &amp; Loisirs ». Partout ailleurs la fiche les passe a v-html, qui les
   * decode au passage ; l'adresse et le nom du club, eux, sont affiches en
   * texte, ou l'entite restait visible telle quelle.
   *
   * Le decodage se fait dans un <textarea> : son contenu n'est jamais
   * interprete comme du balisage, donc rien d'executable ne peut en sortir. Et
   * le resultat reste insere en texte ({{ }}), jamais en v-html.
   */
  const decoderEntites = (valeur) => {
    const texte = String(valeur ?? '');
    if (!texte.includes('&')) return texte;
    const zone = document.createElement('textarea');
    zone.innerHTML = texte;
    return zone.value;
  };

  const fdClubNom = computed(() => decoderEntites(od.value.clubName).trim());

  const fdAdresse = computed(() => {
    const d = od.value;
    return [d.finalAddress, [d.finalPostcode, d.finalCity].filter(Boolean).join(' ')]
      .map((x) => decoderEntites(x).replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join(', ');
  });

  /**
   * L'offre ne porte pas de coordonnees : la carte comme l'itineraire partent
   * du texte de l'adresse. C'est aussi pourquoi le decodage compte ici — une
   * recherche sur « l&#039;Ecole » ne trouve pas la rue.
   *
   * Sur iPhone, maps.apple.com bascule tout seul dans Plans ; ailleurs on
   * ouvre Google Maps, qui prend le relais de l'application installee.
   */
  const fdLienCarte = computed(() => {
    const q = encodeURIComponent(fdAdresse.value);
    return isIOS()
      ? `https://maps.apple.com/?q=${q}`
      : `https://www.google.com/maps/search/?api=1&query=${q}`;
  });

  const fdCarteApercu = computed(
    () => `https://maps.google.com/maps?q=${encodeURIComponent(fdAdresse.value)}&hl=fr&z=15&output=embed`,
  );

  /** Initiales du club, pour la pastille de l'organisateur. */
  const fdInitiales = computed(() => fdClubNom.value
    .split(/[\s,]+/).filter(Boolean).slice(0, 3).map((m) => m[0].toUpperCase()).join(''));

  /** Photos de l'offre, dédoublonnées. */
  const fdPhotos = computed(() => [...new Set(offerDetails.value?.images || [])].filter(Boolean));
  const fdPhotoActive = ref(0);

  const fdDescriptionOuverte = ref(false);

  /** Partage natif : feuille de partage du système, repli presse-papier. */
  const fdPartager = () => {
    const lien = offerDetails.value?.actions?.shareLink
      || `https://www.ouicsport.fr/offer/${offerDetails.value?.offerId || ''}`;
    shareOffer({
      title: od.value.titleOffers || 'OuiCSport',
      text: 'Découvre cette sortie sur OuiCSport',
      url: lien,
    });
  };

  return { offerDetails, loading, error, isLoggedIn, currentMonth, currentYear, formattedDates, imagesValid, idofferData, dateSelected, googleDriveLinkFormatted,
           imageOriginale, od, fdPartager, fdAvis, fdDates, fdEstSejour, fdPeriode, fdDateChoisie, fdChoisirDate, fdCible,
           fdNiveau, fdInclus, fdOptions, fdAdresse, fdClubNom, fdLienCarte, fdCarteApercu, fdInitiales, fdPhotos, fdPhotoActive, fdDescriptionOuverte,
           apiImg, sportBanner, isNative };
},
mounted() {
  this.checkScreenWidth();
  window.addEventListener('resize', this.checkScreenWidth);
},

};
</script>

<style>
/* =========================================================================
   Styles du SITE WEB — restaurés à l'identique.
   =========================================================================
   Ce composant portait deux blocs <style> : l'un contenait ces règles du
   site, l'autre les anciennes surcharges natives. En remplaçant le premier
   par la feuille de l'application, ces douze règles avaient disparu — le
   contrôle de non-régression du CSS web l'a signalé, elles sont reprises
   ici telles quelles depuis l'empreinte de référence.
   Ne pas y toucher : elles n'ont d'effet que sur le site.
   ========================================================================= */
.reservation-button button {padding:10px 20px;border:none;cursor:not-allowed;transition:background-color .3s ease,transform .2s ease}
.reservation-button button:not(:disabled) {cursor:pointer}
.reservation-button button:hover:not(:disabled) {transform:scale(1.05)}
.login-warning {color:red;font-size:clamp(1rem,2vw,1.3rem);margin-top:10px}
.modal-overlay {position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.8);display:flex;justify-content:center;align-items:center;z-index:1000}
.modal-content {background:#fff;padding:20px;border-radius:8px;max-width:80%;max-height:80%;overflow-y:auto}
.close-button {background:none;border:none;font-size:1.5rem;cursor:pointer;position:absolute;top:10px;right:10px}
.modal-images {display:flex;flex-direction:column}
.modal-image {margin-bottom:10px}
.modal-image img {max-width:100%;height:auto}
.small-spinner[data-v-SCOPE] {width:16px;height:16px;border:2px solid #f3f3f3;border-top:2px solid #e74c3c;border-radius:50%;animation:spin-ba54825a 1s linear infinite}
.participants-form[data-v-SCOPE] {margin-top:15px;padding:30px 25px 25px;background:hsla(0,0%,100%,.4);border:2px solid #e74c3c;border-radius:20px;animation:slideDown-ba54825a .4s cubic-bezier(.16,1,.3,1)}
.loading-spinner[data-v-SCOPE] {width:40px;height:40px;border:4px solid #f3f3f3;border-top:4px solid #e74c3c;border-radius:50%;animation:spin-ba54825a 1s linear infinite;margin:0 auto 10px}
@media (max-width:768px) {
  .reservation-button button {padding:15px 12px;font-size:1rem;width:80%}
}
@media (max-width:480px) {
  .reservation-button button {padding:15px 12px;font-size:1rem;width:100%}
}
</style>

<style>
/* =========================================================================
   FICHE D'ACTIVITÉ — feuille de l'application, écrite de zéro
   =========================================================================
   Balisage natif distinct de celui du site : rien n'est hérité ni combattu.
   Sélecteurs préfixés `body.is-native` pour que le contrôle automatique
   vérifie que le site reste intact.
   ========================================================================= */

body.is-native .fd-page,
body.is-native .fd-page *,
body.is-native .fd-page *::before,
body.is-native .fd-page *::after {
  box-sizing: border-box;
}

/* Remise à zéro au niveau des éléments : la feuille du site peint `header`
   en blanc et impose ses réglages à h1, h2, p, ul, li, button, a, img —
   qui rattraperaient mes éléments malgré un balisage neuf. */
body.is-native .fd-page header,
body.is-native .fd-page section,
body.is-native .fd-page h1,
body.is-native .fd-page h2,
body.is-native .fd-page p,
body.is-native .fd-page ul,
body.is-native .fd-page li,
body.is-native .fd-page span,
body.is-native .fd-page div,
body.is-native .fd-page button,
body.is-native .fd-page a,
body.is-native .fd-page img {
  all: unset;
  display: revert;
  /* `all: unset` retire le `overflow: clip` que le navigateur applique
     aux éléments remplacés : sans lui, le débordement d'`object-fit:
     cover` se peint HORS du cadre de l'image. */
  overflow: clip;
  box-sizing: border-box;
}

body.is-native .fd-page {
  --fd-bg: #F5F0E6;
  --fd-surface: #EFE8DA;
  --fd-raised: #E5DDD0;
  --fd-dark: #2B231F;
  --fd-ink: #28201C;
  --fd-ink-2: #6B5E54;
  --fd-ink-3: #8C7E73;
  --fd-on-dark: #FAF6EE;
  --fd-red: #DD4124;
  --fd-red-soft: rgba(221, 65, 36, 0.12);
  --fd-hair: rgba(107, 94, 84, 0.14);
  --fd-or: #E5A100;
  --fd-vert: #2E7D32;
  --fd-barre: 72px;

  padding-bottom: calc(var(--fd-barre) + 24px);
  background: var(--fd-bg);
  font-family: 'Dosis', system-ui, sans-serif;
  color: var(--fd-ink);
  -webkit-user-select: none;
  user-select: none;
}

/* ---------- Barre de navigation ---------- */

body.is-native .fd-page .fd-nav {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 16px 8px;
  background: var(--fd-bg);
  border-bottom: 1px solid var(--fd-hair);
}

body.is-native .fd-page .fd-nav__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: var(--fd-ink);
  background: var(--fd-surface);
  border: 1px solid var(--fd-hair);
  border-radius: 22px;
  cursor: pointer;
  transition: transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .fd-page .fd-nav__btn:active { transform: scale(0.94); }

body.is-native .fd-page .fd-nav__sport {
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--fd-red);
  background: var(--fd-red-soft);
  border-radius: 12px;
}

/* ---------- Photo : nette et contenue, sur son propre flou ---------- */

body.is-native .fd-page .fd-photo { padding: 12px 16px 0; }

body.is-native .fd-page .fd-photo__frame {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 220px;
  overflow: hidden;
  background: var(--fd-raised);
  border-radius: 20px;
}

/* Couverture pleine largeur.
   La photo est chargée dans sa définition d'origine (imageOriginale), et non
   dans la variante 414x219 : la moitié du catalogue est en 1920x1080, ce qui
   permet une vraie couverture. Les offres dont l'original ne fait que 388x200
   seront un peu douces à cette taille — c'est un compromis assumé, une photo
   minuscule flottant dans un cadre faisait bien plus étrange. */
body.is-native .fd-page .fd-photo__main {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

body.is-native .fd-page .fd-photo__tag {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--fd-on-dark);
  background: rgba(43, 35, 31, 0.88);
  border-radius: 8px;
}

body.is-native .fd-page .fd-photo__strip {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
}

body.is-native .fd-page .fd-photo__strip::-webkit-scrollbar { display: none; }

body.is-native .fd-page .fd-photo__thumb {
  flex: 0 0 auto;
  width: 80px;
  height: 56px;
  overflow: hidden;
  border: 2px solid transparent;
  border-radius: 12px;
  opacity: 0.65;
  cursor: pointer;
  transition: opacity 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
              border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .fd-page .fd-photo__thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

body.is-native .fd-page .fd-photo__thumb.is-on {
  opacity: 1;
  border-color: var(--fd-red);
}

/* ---------- En-tête ---------- */

body.is-native .fd-page .fd-head { padding: 14px 16px 4px; }

body.is-native .fd-page .fd-head__title {
  display: block;
  margin-bottom: 10px;
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 26px;
  line-height: 1.08;
  letter-spacing: 0.3px;
  color: var(--fd-ink);
}

body.is-native .fd-page .fd-head__rating {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

body.is-native .fd-page .fd-stars {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  color: var(--fd-or);
}

body.is-native .fd-page .fd-head__score {
  font-size: 16px;
  font-weight: 800;
  color: var(--fd-ink);
}

body.is-native .fd-page .fd-head__count {
  font-size: 14px;
  font-weight: 600;
  color: var(--fd-ink-2);
}

body.is-native .fd-page .fd-head__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

body.is-native .fd-page .fd-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 700;
  color: var(--fd-ink-2);
  background: var(--fd-surface);
  border: 1px solid var(--fd-hair);
  border-radius: 12px;
}

/* ---------- Cartes de section ---------- */

body.is-native .fd-page .fd-card {
  margin: 16px 16px 0;
  padding: 16px;
  background: var(--fd-surface);
  border: 1px solid var(--fd-hair);
  border-radius: 20px;
}

body.is-native .fd-page .fd-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

body.is-native .fd-page .fd-card__title {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 20px;
  line-height: 1.1;
  text-transform: uppercase;
  color: var(--fd-ink);
}

body.is-native .fd-page .fd-card__badge {
  flex: 0 0 auto;
  padding: 3px 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--fd-ink-2);
  background: var(--fd-raised);
  border-radius: 8px;
}

body.is-native .fd-page .fd-card__link {
  flex: 0 0 auto;
  font-size: 14px;
  font-weight: 700;
  color: var(--fd-red);
  cursor: pointer;
}

body.is-native .fd-page .fd-card__note {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--fd-ink-3);
}

/* ---------- Dates ---------- */

body.is-native .fd-page .fd-dates {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

body.is-native .fd-page .fd-date {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  background: var(--fd-bg);
  border-radius: 14px;
}

body.is-native .fd-page .fd-date.is-full { opacity: 0.55; }

/* La carte est désormais portée par un bouton à l'intérieur du <li>, et non
   plus par le <li> lui-même : sans cette règle, le `display: revert` du reset
   rend au <li> son statut d'élément de liste, donc sa puce. */
body.is-native .fd-page .fd-dates > li {
  display: block;
  list-style: none;
}

/* Date sélectionnable : le bouton porte la carte entière pour offrir une
   cible tactile pleine largeur, bien au-delà des 44px requis. */
body.is-native .fd-page .fd-date--choix {
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: 1.5px solid transparent;
  transition: border-color 140ms ease, background-color 140ms ease, transform 140ms ease;
  -webkit-tap-highlight-color: transparent;
}

body.is-native .fd-page .fd-date--choix:active:not(.is-full) {
  transform: scale(0.985);
}

body.is-native .fd-page .fd-date--choix.is-selected {
  border-color: var(--fd-red);
  background: #FBEDE9;
}

/* Une date complète reste lisible mais n'est plus une cible. */
body.is-native .fd-page .fd-date--choix.is-full {
  cursor: default;
}

body.is-native .fd-page .fd-date--choix:focus-visible {
  outline: 2px solid var(--fd-red);
  outline-offset: 2px;
}

body.is-native .fd-page .fd-date__left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

body.is-native .fd-page .fd-date__day {
  font-size: 16px;
  font-weight: 800;
  color: var(--fd-ink);
  font-variant-numeric: tabular-nums;
}

body.is-native .fd-page .fd-date__spots {
  font-size: 13px;
  font-weight: 600;
  color: var(--fd-red);
}

body.is-native .fd-page .fd-date.is-full .fd-date__spots { color: var(--fd-ink-3); }

body.is-native .fd-page .fd-date__price {
  flex: 0 0 auto;
  font-size: 18px;
  font-weight: 800;
  color: var(--fd-ink);
  font-variant-numeric: tabular-nums;
}

/* ---------- Description ---------- */

body.is-native .fd-page .fd-desc {
  position: relative;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  color: var(--fd-ink);
  word-break: break-word;
}

body.is-native .fd-page .fd-desc :is(p, div, span) { display: revert; }
body.is-native .fd-page .fd-desc br { display: revert; }

body.is-native .fd-page .fd-desc.is-clipped {
  max-height: 132px;
  overflow: hidden;
}

body.is-native .fd-page .fd-desc.is-clipped::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 56px;
  background: linear-gradient(180deg, rgba(239, 232, 218, 0), var(--fd-surface));
  pointer-events: none;
}

body.is-native .fd-page .fd-more {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 44px;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 800;
  color: var(--fd-red);
  cursor: pointer;
}

body.is-native .fd-page .fd-more svg { transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1); }

/* ---------- Inclus et options ---------- */

body.is-native .fd-page .fd-inclus {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

body.is-native .fd-page .fd-inclus__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--fd-ink);
}

body.is-native .fd-page .fd-inclus__check,
body.is-native .fd-page .fd-inclus__plus {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 10px;
}

body.is-native .fd-page .fd-inclus__check {
  color: var(--fd-vert);
  background: rgba(46, 125, 50, 0.12);
}

body.is-native .fd-page .fd-inclus__plus {
  font-size: 15px;
  font-weight: 800;
  color: var(--fd-red);
  background: var(--fd-red-soft);
}

/* ---------- Programme (aperçu PDF) ---------- */

body.is-native .fd-page .fd-pdf {
  overflow: hidden;
  background: var(--fd-bg);
  border: 1px solid var(--fd-hair);
  border-radius: 14px;
}

body.is-native .fd-page .fd-pdf iframe {
  display: block;
  width: 100%;
  height: 420px;
  border: 0;
}

/* ---------- Organisateur et lieu ---------- */

body.is-native .fd-page .fd-club {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

body.is-native .fd-page .fd-club__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 17px;
  letter-spacing: 0.5px;
  color: var(--fd-on-dark);
  background: var(--fd-dark);
  border-radius: 24px;
}

body.is-native .fd-page .fd-club__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

body.is-native .fd-page .fd-club__name {
  font-size: 17px;
  font-weight: 800;
  color: var(--fd-ink);
}

body.is-native .fd-page .fd-club__sub {
  font-size: 14px;
  font-weight: 600;
  color: var(--fd-ink-2);
}

body.is-native .fd-page .fd-adresse {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 44px;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--fd-ink);
  background: var(--fd-bg);
  border-radius: 12px;
  cursor: pointer;
}

body.is-native .fd-page .fd-adresse svg { flex: 0 0 auto; margin-top: 2px; color: var(--fd-red); }

body.is-native .fd-page .fd-lieu {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

body.is-native .fd-page .fd-adresse__texte { min-width: 0; }

/* Hauteur fixe : la carte est un reperage, pas un outil de navigation. Elle
   reserve sa place avant le chargement de l'iframe, ce qui evite que le
   contenu situe dessous saute une fois la carte arrivee. */
body.is-native .fd-page .fd-carte {
  position: relative;
  display: block;
  height: 150px;
  overflow: hidden;
  background: var(--fd-bg);
  border-radius: 12px;
}

body.is-native .fd-page .fd-carte__vue {
  width: 100%;
  height: 100%;
  border: 0;
  pointer-events: none;
}

body.is-native .fd-page .fd-carte__pastille {
  /* En haut a droite : en bas, la pastille masquerait l'attribution Google et
     le lien vers ses conditions, que la carte doit laisser lisibles. */
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 800;
  color: var(--fd-on-dark);
  background: var(--fd-dark);
  border-radius: 999px;
}

/* ---------- Avis ---------- */

body.is-native .fd-page .fd-note {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px dashed rgba(107, 94, 84, 0.22);
}

body.is-native .fd-page .fd-note__big {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 40px;
  line-height: 1;
  color: var(--fd-ink);
}

body.is-native .fd-page .fd-note__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

body.is-native .fd-page .fd-note__sub {
  font-size: 13px;
  font-weight: 600;
  color: var(--fd-ink-2);
}

body.is-native .fd-page .fd-avis {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

body.is-native .fd-page .fd-avis__item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: var(--fd-bg);
  border-radius: 12px;
}

body.is-native .fd-page .fd-avis__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

body.is-native .fd-page .fd-avis__who {
  font-size: 15px;
  font-weight: 700;
  color: var(--fd-ink);
}

body.is-native .fd-page .fd-avis__when {
  font-size: 12px;
  font-weight: 600;
  color: var(--fd-ink-3);
}

body.is-native .fd-page .fd-avis__text {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--fd-ink-2);
}

body.is-native .fd-page .fd-legal {
  padding: 18px 16px 0;
  text-align: center;
}

body.is-native .fd-page .fd-legal a {
  font-size: 13px;
  font-weight: 600;
  color: var(--fd-ink-3);
  text-decoration: underline;
  cursor: pointer;
}

/* ---------- Barre d'action ---------- */

body.is-native .fd-page .fd-bar {
  position: fixed;
  left: 0;
  right: 0;
  /* Posée au-dessus de la barre d'onglets, jamais par-dessus. */
  bottom: calc(var(--oc-tabbar-h) + var(--oc-safe-bottom));
  z-index: 940;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  height: var(--fd-barre);
  padding: 0 16px;
  background: var(--fd-dark);
  border-top: 1px solid rgba(250, 246, 238, 0.12);
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.22);
}

body.is-native .fd-page .fd-bar__price {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

body.is-native .fd-page .fd-bar__label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--fd-ink-3);
}

body.is-native .fd-page .fd-bar__value {
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  color: var(--fd-on-dark);
  font-variant-numeric: tabular-nums;
}

body.is-native .fd-page .fd-bar__value span { font-size: 15px; font-weight: 600; }

body.is-native .fd-page .fd-bar__cta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-height: 48px;
  padding: 0 22px;
  font-size: 17px;
  font-weight: 800;
  color: var(--fd-on-dark);
  background: var(--fd-red);
  border-radius: 24px;
  cursor: pointer;
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .fd-page .fd-bar__cta:active { transform: scale(0.96); }

/* ---------- Chargement et erreur ---------- */

body.is-native .fd-page--wait {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: calc(env(safe-area-inset-top, 0px) + 24px) 16px 0;
}

body.is-native .fd-page .fd-ghost {
  display: block;
  background: linear-gradient(90deg, var(--fd-surface) 25%, var(--fd-raised) 37%, var(--fd-surface) 63%);
  background-size: 400% 100%;
  animation: fd-shimmer 1.4s ease infinite;
  border-radius: 14px;
}

body.is-native .fd-page .fd-ghost--photo { height: 220px; border-radius: 20px; }
body.is-native .fd-page .fd-ghost--line { height: 22px; }
body.is-native .fd-page .fd-ghost--short { width: 55%; }
body.is-native .fd-page .fd-ghost--block { height: 180px; border-radius: 20px; }

@keyframes fd-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

body.is-native .fd-page .fd-erreur {
  padding: 40px 8px;
  font-size: 16px;
  line-height: 1.5;
  color: var(--fd-ink-2);
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  body.is-native .fd-page .fd-ghost { animation: none; }
}
</style>
